import { gql } from '@urql/core';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { DEFAULT_LIMIT } from './constants';

export const adPlatformAnalyticsFeatureKeys = createQueryKeys('adPlatformAnalyticsFeature', {
  dashboard: ({ siteId, startAt, endAt, presetDate, platforms, limit }) => [
    {
      siteId,
      startAt,
      endAt,
      presetDate,
      platforms,
      limit,
    },
  ],
  adDetail: ({ siteId, adId, startAt, endAt, presetDate, goalId }) => [
    {
      siteId,
      adId,
      startAt,
      endAt,
      presetDate,
      goalId,
    },
  ],
  adPreview: ({ siteId, adId }) => [
    {
      siteId,
      adId,
    },
  ],
});

const adPlatformAnalyticsDocument = gql`
  query AdPlatformAnalytics(
    $siteId: Int!
    $startAt: Int
    $endAt: Int
    $presetDate: String
    $platforms: [AdPlatformSource!]
    $limit: Int
  ) {
    adPlatformAnalytics(
      siteId: $siteId
      startAt: $startAt
      endAt: $endAt
      presetDate: $presetDate
      platforms: $platforms
      limit: $limit
    ) {
      summary {
        impressions
        clicks
        spendMicros
        conversions
        conversionValueMicros
        roas
        ctr
        cpcMicros
        cpaMicros
      }
      daily {
        date
        impressions
        clicks
        spendMicros
        conversions
        conversionValueMicros
        roas
      }
      platformTotals {
        platform
        impressions
        clicks
        spendMicros
        conversions
        roas
      }
      topCampaigns {
        id
        platformId
        name
        platform
        impressions
        clicks
        spendMicros
        conversions
        roas
      }
      topAds {
        id
        platformId
        name
        platform
        impressions
        clicks
        spendMicros
        conversions
        roas
      }
      connections {
        connectionId
        accountId
        platform
        oldestImportedDataAt
        lastEntitiesSyncedAt
        lastMetricsSyncedAt
        lastSyncedAt
        lastSyncedDate
      }
    }
  }
`;

const adPlatformAdAnalyticsDocument = gql`
  query AdPlatformAdAnalytics(
    $siteId: Int!
    $adId: Int!
    $goalId: Int
    $startAt: Int
    $endAt: Int
    $presetDate: String
  ) {
    adPlatformAdAnalytics(
      siteId: $siteId
      adId: $adId
      goalId: $goalId
      startAt: $startAt
      endAt: $endAt
      presetDate: $presetDate
    ) {
      ad {
        id
        platformId
        name
        platform
        campaignName
      }
      siteMetrics {
        visitors
        views
        ceConversions
      }
    }
  }
`;

const adPlatformAdPreviewDocument = gql`
  query AdPlatformAdPreview($siteId: Int!, $adId: Int!) {
    adPlatformAdPreview(siteId: $siteId, adId: $adId) {
      ad {
        id
        platformId
        name
        platform
        campaignName
      }
      preview {
        previewSupported
        template
        placementLabel
        advertiserName
        headline
        headlines
        body
        descriptions
        destinationUrl
        displayUrl
        path1
        path2
        imageUrl
        videoUrl
        mediaType
      }
    }
  }
`;

export function adPlatformAnalyticsQuery({ siteId, startAt, endAt, presetDate, platforms, limit = DEFAULT_LIMIT }) {
  const variables = {
    siteId,
    limit,
  };

  if (Number.isFinite(startAt)) variables.startAt = startAt;
  if (Number.isFinite(endAt)) variables.endAt = endAt;
  if (presetDate) variables.presetDate = presetDate;
  if (Array.isArray(platforms) && platforms.length > 0) variables.platforms = platforms;

  return {
    ...adPlatformAnalyticsFeatureKeys.dashboard({ siteId, startAt, endAt, presetDate, platforms, limit }),
    enabled: Number.isFinite(siteId),
    useErrorBoundary: false,
    refetchOnWindowFocus: false,
    meta: {
      query: adPlatformAnalyticsDocument,
      variables,
    },
  };
}

export function adPlatformAdAnalyticsQuery({ siteId, adId, goalId, startAt, endAt, presetDate }) {
  const variables = {
    siteId,
    adId,
  };

  if (Number.isFinite(goalId)) variables.goalId = goalId;
  if (Number.isFinite(startAt)) variables.startAt = startAt;
  if (Number.isFinite(endAt)) variables.endAt = endAt;
  if (presetDate) variables.presetDate = presetDate;

  return {
    ...adPlatformAnalyticsFeatureKeys.adDetail({ siteId, adId, goalId, startAt, endAt, presetDate }),
    enabled: Number.isFinite(siteId) && Number.isFinite(adId),
    useErrorBoundary: false,
    refetchOnWindowFocus: false,
    meta: {
      query: adPlatformAdAnalyticsDocument,
      variables,
    },
  };
}

export function adPlatformAdPreviewQuery({ siteId, adId }) {
  const variables = {
    siteId,
    adId,
  };

  return {
    ...adPlatformAnalyticsFeatureKeys.adPreview({ siteId, adId }),
    enabled: Number.isFinite(siteId) && Number.isFinite(adId),
    useErrorBoundary: false,
    refetchOnWindowFocus: false,
    meta: {
      query: adPlatformAdPreviewDocument,
      variables,
    },
  };
}
